import * as React from 'react'
import { Heading, Pane, PaneProps, Paragraph } from 'evergreen-ui'
import { useCustomTheme } from '/fiweb/lib/theme'
import { useBreakpoint } from '/fiweb/lib'
import { ResponsiveCardContainer } from '/~/components'
import { useGoogleTagManagerInView } from '/~/utils/googleTagManager'
import { ClientCampaignCard } from '/fiweb/components/CampaignCard'
import { AdCard, CampaignCard, CampaignCardWrapper } from '/~/components/Cards'
import { useLocation } from 'react-router'
import { client } from '/~/lib/issuer-client/client'

export type CardSectionName =
  | 'finished'
  | 'ongoing'
  | 'upcoming'
  | 'comingSoon'
  | 'frontpageOngoing'
  | 'frontpageComingSoon'

interface Props {
  name: CardSectionName
  title: React.ReactNode
  description?: string
  cards: Array<ClientCampaignCard>
  ctaButton?: React.ReactNode
  ignoreAdCard?: boolean
  loading?: boolean
  paneProps?: PaneProps
  id?: string
  renderCard?: (id: string) => React.ReactNode
}

const CardSection = ({
  id,
  ignoreAdCard,
  name,
  title,
  description,
  cards,
  ctaButton,
  loading,
  paneProps,
  renderCard,
}: Props) => {
  const theme = useCustomTheme()
  const { medium, large } = useBreakpoint()
  const { elementRef } = useGoogleTagManagerInView(cards, name)

  const { hash } = useLocation()
  const scrollRef = React.useRef<HTMLDivElement>()
  const [hasScrolled, setHasScrolled] = React.useState(false)

  const isLoading = () => {
    if (!hash) {
      return false
    }
    const newQueries = client.getObservableQueries()

    return Array.from(newQueries.keys()).reduce((result, key) => {
      const q = newQueries.get(key)
      return !!(!q.getLastResult() || q.getCurrentResult().loading) || result
    }, false)
  }

  const scrollToElement = () => {
    window.scrollTo({ top: scrollRef.current.getBoundingClientRect().top - 100 })
    setHasScrolled(true)
  }

  React.useEffect(() => {
    if (hash && hash.replace('#', '') === id && !hasScrolled && scrollRef.current) {
      if (!isLoading()) {
        scrollToElement()
      } else {
        let attemptsLeft = 50
        const interval = setInterval(() => {
          if (attemptsLeft-- > 0 && !isLoading()) {
            scrollToElement()
          }
        }, 100)
        return () => clearInterval(interval)
      }
    }
  }, [loading, hasScrolled])

  return (
    <Pane
      key={name}
      width='100%'
      display='flex'
      flexDirection='column'
      alignItems='center'
      ref={elementRef}
      paddingY={medium ? theme.spacing.m : theme.spacing.l}
      {...paneProps}
    >
      <Pane paddingX={30} width='100%' maxWidth={1260} display='flex' flexDirection='column' alignItems='start'>
        <Pane marginBottom={theme.spacing.m} width='100%'>
          <Pane display='flex' justifyContent={medium ? 'center' : 'space-between'} width='100%'>
            {typeof title === 'string' ? (
              <Heading
                size={medium ? 500 : 600}
                textAlign={medium ? 'center' : 'start'}
                marginBottom={theme.spacing.xs}
                ref={scrollRef}
              >
                {title}
              </Heading>
            ) : (
              title
            )}
            {!medium && ctaButton}
          </Pane>

          {description && (
            <Paragraph size={medium ? 400 : 500} textAlign={medium ? 'center' : 'start'}>
              {description}
            </Paragraph>
          )}
        </Pane>
        <ResponsiveCardContainer paddingBottom={theme.spacing.m}>
          {loading ? (
            <>
              {[...new Array(medium ? 1 : large ? 2 : 3)].map((_, i) => (
                <CampaignCardWrapper key={i}>
                  <CampaignCard loading />
                </CampaignCardWrapper>
              ))}
            </>
          ) : (
            <>
              {cards.map((card) => (
                <CampaignCardWrapper key={card.id}>
                  {renderCard?.(card.id) || <CampaignCard card={card} />}
                </CampaignCardWrapper>
              ))}
              {!ignoreAdCard && (
                <CampaignCardWrapper key='adCard'>
                  <AdCard />
                </CampaignCardWrapper>
              )}
            </>
          )}
        </ResponsiveCardContainer>
      </Pane>

      {medium && ctaButton}
    </Pane>
  )
}

export default CardSection
